import React, { useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import styles from './index.module.scss'
import { Alert, Button, message, Modal, Pagination, Progress } from 'antd'
import { useIntl } from 'react-intl'
import html2canvas from 'html2canvas'
import { PDFDocument } from 'pdf-lib'
import hmLogo from '../../assets/img/hm_logo.png'
import bstLogo from '../../assets/img/bst.png'

interface ProductItem {
  main_group?: string
  part_name?: string
  quality_name?: string
  quality_oe?: string
  quality_price?: string | number
  code?: string
  codes?: string[]
  img?: string
  params?: string
  remark?: string
  warranty?: string
}

interface PdfconProps {
  list: ProductItem[]
  setOpen: (open: boolean) => void
  colSelect: string[]
  title?: string
  inlogo: string
  dph?: string
  partnameData: Record<string, Record<string, string>>
  partlangkey: string
  shownum: number
}

interface CardImage {
  url: string
  width: number
  height: number
}

const A4_WIDTH = 210
const A4_HEIGHT = 297
const PAGE_MARGIN = 10
const CONTENT_WIDTH = A4_WIDTH - PAGE_MARGIN * 2
const EXPORT_WIDTH = 1000
const CANVAS_SCALE = 1.2
const JPEG_QUALITY = 1
const HEADER_HEIGHT_PX = 150
const CARD_GAP_PX = 10
const PDF_URL_REVOKE_DELAY = 1000
const LARGE_DATA_THRESHOLD = 200
const PREVIEW_PAGE_SIZE = 24

const HM_HOSTS = ['webtest.maiqipei.com', 'gzhmqy.maiqipei.com', 'gzhmqy.com', '300']
const BST_HOSTS = ['bmtsr.maiqipei.com', 'bmtsr.xiaomaiqipei.com']
const SEN_HOSTS = ['senp.maiqipei.com', 'senp.xiaomaiqipei.com', '300']

const includesHost = (origin: string, hosts: string[]) => hosts.some((host) => origin.includes(host))
const getTitleText = (title?: string) => title?.split('>').join(' ') || ''
const getDownloadName = (title?: string) => {
  const name = title?.split('>').slice(0, 3).join('-').trim() || 'products'
  return `${name.replace(/[\\/:*?"<>|]/g, '-')}.pdf`
}

const Pdfcon: React.FC<PdfconProps> = (props) => {
  const intl = useIntl()
  const { list, setOpen, colSelect, title, inlogo, dph, partnameData, partlangkey, shownum } = props

  const [loading, setLoading] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [processedCount, setProcessedCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [previewPage, setPreviewPage] = useState(1)
  const cancelledRef = useRef(false)
  const totalCountRef = useRef(0)
  const cleanupTasksRef = useRef(new Set<() => void>())
  const objectUrlsRef = useRef(new Set<string>())

  const origin = window.location.origin
  const ishm = includesHost(origin, HM_HOSTS)
  const isdp = origin.includes('dpatp')
  const isSen = includesHost(origin, SEN_HOSTS)
  const logo = ishm ? hmLogo : includesHost(origin, BST_HOSTS) ? bstLogo : inlogo
  const titleText = getTitleText(title)
  const jsPDF = (window as any).jspdf?.jsPDF
  const isCardLayout = shownum === 2 || shownum === 3
  const usePaginatedPreview = list.length > LARGE_DATA_THRESHOLD
  const useIndependentCardExport = shownum === 2 && ishm
  const storedLang = window.localStorage.getItem('lang')
  const lang = storedLang === 'zh' ? 'cn' : storedLang
  const getTranslatedPartName = (item: ProductItem) => {
    if(!item.part_name || !colSelect.includes('part_name')) return ''
    return partnameData[item.part_name]?.[partlangkey] || ''
  }

  const classList = useMemo<ProductItem[]>(() => {
    // hm要求按照编码首位排序
    if(ishm) return list

    const groupedItems = list.reduce<Record<string, ProductItem[]>>((result, item) => {
      const group = item.main_group || ''
      result[group] = result[group] || []
      result[group].push(item)
      return result
    }, {})
    const columns = isdp ? 3 : 4

    return Object.entries(groupedItems).flatMap(([group, items]) => {
      const placeholderCount = (columns - (items.length % columns)) % columns
      return [
        ...items,
        ...Array.from({ length: placeholderCount }, () => ({ main_group: group })),
      ]
    })
  }, [isdp, ishm, list])

  const cardItems = shownum === 2 ? classList : list
  const previewItems = useMemo(() => {
    if(!usePaginatedPreview) return cardItems
    const start = (previewPage - 1) * PREVIEW_PAGE_SIZE
    return cardItems.slice(start, start + PREVIEW_PAGE_SIZE)
  }, [cardItems, previewPage, usePaginatedPreview])

  useEffect(() => {
    setPreviewPage(1)
  }, [list, shownum])

  useEffect(() => {
    const cleanupTasks = cleanupTasksRef.current
    const objectUrls = objectUrlsRef.current
    return () => {
      cancelledRef.current = true
      cleanupTasks.forEach((cleanup) => cleanup())
      cleanupTasks.clear()
      objectUrls.forEach((url) => URL.revokeObjectURL(url))
      objectUrls.clear()
    }
  }, [])

  const ensureExportActive = () => {
    if(cancelledRef.current) throw new Error('PDF_EXPORT_CANCELLED')
  }

  const updateExportProgress = (percent: number, processed?: number) => {
    if(cancelledRef.current) return
    setProgress(percent)
    if(processed !== undefined) setProcessedCount(Math.min(processed, totalCountRef.current))
  }

  const registerCleanup = (cleanupAction: () => void) => {
    let cleaned = false
    const cleanup = () => {
      if(cleaned) return
      cleaned = true
      cleanupTasksRef.current.delete(cleanup)
      cleanupAction()
    }
    cleanupTasksRef.current.add(cleanup)
    return cleanup
  }

  const createManagedRoot = (rootContainer: HTMLElement, ownerContainer = rootContainer) => {
    const root = createRoot(rootContainer)
    const cleanup = registerCleanup(() => {
      root.unmount()
      ownerContainer.remove()
    })
    return { root, cleanup }
  }

  const renderStyleThreeCard = (item: ProductItem, index: number) => (
    <div className={styles['product-card-3']} key={`${previewPage}-${index}`}>
      <div className={styles['img-wrap'] + ' flex-c-c'}>
        {colSelect.includes('img') && item.img ? (
          <img alt="" className={styles['img-1']} src={item.img} />
        ) : null}
      </div>
      <div className={styles['product-card-3-content'] + ' p10'}>
        {colSelect.includes('part_name') ? <h3>{item.part_name}</h3> : null}
        {partlangkey !== lang ? <h3>{getTranslatedPartName(item)}</h3> : null}
        {colSelect.includes('quality_name') ? (
          <div className={styles['product-props']}>
            <span style={{ width: 50 }} className={styles['prop-label']}>{intl.formatMessage({ id: '品牌' })}:</span>
            <span className={styles['prop-value']}>{item.quality_name}</span>
          </div>
        ) : null}
        {colSelect.includes('quality_oe') ? (
          <div className={styles['product-props']}>
            <span style={{ width: 50 }} className={styles['prop-label']}>{intl.formatMessage({ id: 'OE' })}:</span>
            <span className={styles['prop-value']}>{item.quality_oe}</span>
          </div>
        ) : null}
        {colSelect.includes('params') ? (
          <div className={styles['product-props']}>
            <span style={{ width: 50 }} className={styles['prop-label']}>
              {isSen ? intl.formatMessage({ id: '车型' }) : intl.formatMessage({ id: '规格' })}:
            </span>
            <span className={styles['prop-value']} style={{ wordBreak: 'break-all' }}>{item.params}</span>
          </div>
        ) : null}
        {colSelect.includes('remark') ? (
          <div className={styles['product-props']}>
            <span style={{ width: 50 }} className={styles['prop-label']}>
              {isSen ? intl.formatMessage({ id: '规格' }) : intl.formatMessage({ id: '备注' })}:
            </span>
            <span className={styles['prop-value']}>{item.remark}</span>
          </div>
        ) : null}
        <div className="flex-s-c" style={{ fontSize: 15, fontWeight: 500 }}>
          <div>{isSen ? intl.formatMessage({ id: '自编码' }) : intl.formatMessage({ id: '编码' })}:</div>
          {colSelect.includes('code') && item.codes?.length
            ? item.codes.map((code, codeIndex) => <div key={codeIndex}>{code}</div>)
            : null}
        </div>
      </div>
    </div>
  )

  const renderStyleTwoCard = (item: ProductItem, index: number) => (
    <div
      className={`${styles['product-card-1']} ${isdp ? styles['product-card-1-dp'] : ''}`}
      key={`${previewPage}-${index}`}
    >
      <div className="flex plr10" style={{ justifyContent: 'space-between' }}>
        <div>
          {item.part_name ? <div className="p-color">{item.main_group || ''}</div> : null}
          <div>
            {item.part_name ? dph : ''} {colSelect.includes('part_name') && item.part_name ? item.part_name : ''}
          </div>
          {partlangkey !== lang ? <div>{getTranslatedPartName(item)}</div> : null}
          <div>
            {colSelect.includes('code') && item.codes?.length
              ? item.codes.map((code, codeIndex) => <div key={codeIndex}>{code}</div>)
              : null}
          </div>
        </div>
        <img alt="" className={styles['logo-1'] + ' ml10'} src={logo} />
      </div>
      <div className="flex-c-c">
        {colSelect.includes('img') && item.img ? (
          <img alt="" className={styles['img-1']} src={item.img} />
        ) : null}
      </div>
    </div>
  )

  const renderStyleOneCard = (item: ProductItem, index: number) => (
    <div className={styles['product-card']} key={`${previewPage}-${index}`}>
      {colSelect.includes('part_name') ? <h3>{item.part_name}</h3> : null}
      {partlangkey !== lang ? <h3>{getTranslatedPartName(item)}</h3> : null}
      <div style={{ borderTop: '2px solid #ff7f00' }} className="flex-b-c">
        <div>
          {item.part_name && item.main_group ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '分类' })}:</span>
              <span className={styles['prop-value'] + ' p-color'} style={{ color: '#096BED' }}>{item.main_group}</span>
            </div>
          ) : null}
          {colSelect.includes('quality_oe') ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '原厂OE' })}:</span>
              <span className={styles['prop-value']}>{item.quality_oe}</span>
            </div>
          ) : null}
          {colSelect.includes('code') ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '产品编码' })}:</span>
              <span className={styles['prop-value']}>{item.code}</span>
            </div>
          ) : null}
          {colSelect.includes('warranty') ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '质保时间' })}:</span>
              <span className={styles['prop-value']}>{item.warranty}</span>
            </div>
          ) : null}
          {colSelect.includes('quality_name') && colSelect.includes('quality_price') ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '销售价格' })}:</span>
              <span className={styles['prop-value']}>{item.quality_name}: ¥{item.quality_price}</span>
            </div>
          ) : null}
          {colSelect.includes('params') ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '产品参数' })}:</span>
              <span className={styles['prop-value']}>{item.params}</span>
            </div>
          ) : null}
        </div>
        {colSelect.includes('img') ? (
          <div className={styles['product-img']}>
            <img alt="" src={item.img} />
          </div>
        ) : null}
      </div>
    </div>
  )

  const renderCard = shownum === 2 ? renderStyleTwoCard : renderStyleThreeCard

  const downloadFile = (url: string) => {
    ensureExportActive()
    const a = document.createElement('a')
    a.download = getDownloadName(title)
    a.href = url
    document.body.appendChild(a)
    a.click()
    a.remove()
    message.success(intl.formatMessage({ id: '导出成功' }))
  }

  const appendPdfData = async (targetPdf: PDFDocument, pdfData: ArrayBuffer) => {
    ensureExportActive()
    const sourcePdf = await PDFDocument.load(pdfData)
    ensureExportActive()
    const copiedPages = await targetPdf.copyPages(sourcePdf, sourcePdf.getPageIndices())
    copiedPages.forEach((page) => targetPdf.addPage(page))
  }

  async function containerToPdfData(container: HTMLElement, scale = CANVAS_SCALE) {
    if(!jsPDF) throw new Error('jsPDF is unavailable')

    const canvas = await html2canvas(container, {
      allowTaint: true,
      height: container.scrollHeight,
      width: EXPORT_WIDTH,
      scale,
      backgroundColor: '#FFFFFF',
      useCORS: true,
    })
    if(cancelledRef.current) {
      canvas.width = 0
      canvas.height = 0
      ensureExportActive()
    }
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pageContentHeight = A4_HEIGHT - PAGE_MARGIN * 2
    const pageImageHeight = Math.floor(pageContentHeight * canvas.width / CONTENT_WIDTH)
    let renderedHeight = 0

    while(renderedHeight < canvas.height) {
      const currentPageHeight = Math.min(pageImageHeight, canvas.height - renderedHeight)
      const pageCanvas = document.createElement('canvas')
      const pageContext = pageCanvas.getContext('2d')
      if(!pageContext) throw new Error('Canvas context is unavailable')

      pageCanvas.width = canvas.width
      pageCanvas.height = currentPageHeight
      pageContext.drawImage(
        canvas,
        0,
        renderedHeight,
        canvas.width,
        currentPageHeight,
        0,
        0,
        canvas.width,
        currentPageHeight,
      )
      pdf.addImage(
        pageCanvas.toDataURL('image/jpeg', JPEG_QUALITY),
        'JPEG',
        PAGE_MARGIN,
        PAGE_MARGIN,
        CONTENT_WIDTH,
        Math.min(pageContentHeight, CONTENT_WIDTH * currentPageHeight / pageCanvas.width),
      )
      pageCanvas.width = 0
      pageCanvas.height = 0
      renderedHeight += pageImageHeight
      if(renderedHeight < canvas.height) pdf.addPage()
    }

    canvas.width = 0
    canvas.height = 0
    return pdf.output('arraybuffer') as ArrayBuffer
  }

  async function waitForContainerAssets(container: HTMLElement) {
    const images = Array.from(container.querySelectorAll('img'))
    await Promise.all(images.map((image) => {
      if(image.complete) return Promise.resolve()
      return new Promise<void>((resolve) => {
        let timeout = 0
        const cleanup = registerCleanup(() => {
          window.clearTimeout(timeout)
          image.removeEventListener('load', cleanup)
          image.removeEventListener('error', cleanup)
          resolve()
        })
        timeout = window.setTimeout(cleanup, 10000)
        image.addEventListener('load', cleanup, { once: true })
        image.addEventListener('error', cleanup, { once: true })
      })
    }))
    await document.fonts?.ready
    ensureExportActive()
    // 强制浏览器完成布局计算，避免依赖后台标签页会暂停的 requestAnimationFrame。
    void container.offsetHeight
  }

  const yieldToBrowser = () => {
    // 后台标签页的 setTimeout 会被强制降频；后台导出时直接继续任务链。
    if(document.hidden) return Promise.resolve()

    return new Promise<void>((resolve) => {
      const channel = new MessageChannel()
      channel.port1.onmessage = () => {
        channel.port1.close()
        channel.port2.close()
        resolve()
      }
      channel.port2.postMessage(undefined)
    })
  }

  function createLargeExportContainer() {
    const container = document.createElement('div')
    container.style.position = 'fixed'
    container.style.left = '-10000px'
    container.style.top = '0'
    container.style.width = `${EXPORT_WIDTH}px`
    container.style.padding = '40px 0 0'
    container.style.boxSizing = 'border-box'
    container.style.backgroundColor = '#FFFFFF'

    const logoElement = document.createElement('img')
    logoElement.src = logo
    logoElement.style.display = 'block'
    logoElement.style.height = '60px'
    logoElement.style.marginBottom = '25px'
    container.appendChild(logoElement)

    const titleElement = document.createElement('div')
    titleElement.innerText = titleText
    titleElement.style.display = 'inline-block'
    titleElement.style.padding = '5px 15px'
    titleElement.style.borderRadius = '8px'
    titleElement.style.backgroundColor = shownum === 3 ? '#333333' : '#1890ff'
    titleElement.style.color = '#FFFFFF'
    titleElement.style.fontSize = '18px'
    titleElement.style.fontWeight = '500'
    container.appendChild(titleElement)

    const cardsHost = document.createElement('div')
    cardsHost.className = styles['products']
    cardsHost.style.marginTop = '20px'
    container.appendChild(cardsHost)
    document.body.appendChild(container)

    return { container, cardsHost }
  }

  async function appendCardPagePDFs(targetPdf: PDFDocument, items: ProductItem[]) {
    const columns = shownum === 2 && isdp ? 3 : 4
    const rowsPerBatch = shownum === 2 ? (isdp ? 3 : 5) : 3
    const batchSize = columns * rowsPerBatch
    const batchCount = Math.ceil(items.length / batchSize)

    for(let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      const { container, cardsHost } = createLargeExportContainer()
      const { root, cleanup } = createManagedRoot(cardsHost, container)

      try {
        const batchItems = items.slice(batchIndex * batchSize, (batchIndex + 1) * batchSize)
        flushSync(() => root.render(<>{batchItems.map(renderCard)}</>))
        await waitForContainerAssets(container)
        const pdfData = await containerToPdfData(container)
        await appendPdfData(targetPdf, pdfData)
        const processed = Math.min((batchIndex + 1) * batchSize, items.length)
        updateExportProgress(Math.round((processed / items.length) * 90), processed)
        await yieldToBrowser()
        ensureExportActive()
      }finally {
        cleanup()
      }
    }
  }

  async function appendIndependentCardPDFs(targetPdf: PDFDocument, items: ProductItem[]) {
    const columns = 4
    const batchSize = 80
    const batchCount = Math.ceil(items.length / batchSize)

    for(let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      const { container, cardsHost } = createLargeExportContainer()
      const { root, cleanup } = createManagedRoot(cardsHost, container)

      try {
        const batchStart = batchIndex * batchSize
        const batchItems = items.slice(batchStart, batchStart + batchSize)
        flushSync(() => root.render(<>{batchItems.map(renderStyleTwoCard)}</>))
        await waitForContainerAssets(container)
        const children = Array.from(cardsHost.children) as HTMLElement[]
        const pdfData = await generateCardImagePDF(
          children,
          columns,
          '#1890ff',
          (completed) => {
            const totalCompleted = Math.min(batchStart + completed, items.length)
            updateExportProgress(Math.round((totalCompleted / items.length) * 90), totalCompleted)
          },
        )
        await appendPdfData(targetPdf, pdfData)
        await yieldToBrowser()
        ensureExportActive()
      }finally {
        cleanup()
      }
    }
  }

  async function appendStyleOnePDFs(targetPdf: PDFDocument, items: ProductItem[]) {
    const chunkSize = 40
    const batchCount = Math.ceil(items.length / chunkSize)

    for(let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      const tempContainer = document.createElement('div')
      tempContainer.className = styles['products']
      tempContainer.style.position = 'fixed'
      tempContainer.style.left = '-10000px'
      tempContainer.style.top = '0'
      tempContainer.style.backgroundColor = '#FFFFFF'
      if(batchIndex === 0) tempContainer.style.paddingTop = '170px'

      document.body.appendChild(tempContainer)
      const { root, cleanup } = createManagedRoot(tempContainer)

      try {
        const batchItems = items.slice(batchIndex * chunkSize, (batchIndex + 1) * chunkSize)
        flushSync(() => {
          root.render(
            <>
              {batchIndex === 0 ? (
                <>
                  <img alt="" src={logo} style={{ height: 60 }} className={styles['page-logo']} />
                  <div className={styles['sp']}>{titleText}</div>
                </>
              ) : null}
              {batchItems.map(renderStyleOneCard)}
            </>,
          )
        })
        await waitForContainerAssets(tempContainer)
        const pdfData = await containerToPdfData(tempContainer)
        await appendPdfData(targetPdf, pdfData)
        const processed = Math.min((batchIndex + 1) * chunkSize, items.length)
        updateExportProgress(Math.round((processed / items.length) * 90), processed)
        await yieldToBrowser()
        ensureExportActive()
      }finally {
        cleanup()
      }
    }
  }

  async function elementToImage(element: HTMLElement) {
    const rect = element.getBoundingClientRect()
    const width = Math.ceil(rect.width)
    const height = Math.ceil(rect.height)
    const canvas = await html2canvas(element, {
      allowTaint: true,
      height,
      width,
      scale: CANVAS_SCALE,
      backgroundColor: '#FFFFFF',
      useCORS: true,
    })
    if(cancelledRef.current) {
      canvas.width = 0
      canvas.height = 0
      ensureExportActive()
    }
    const image = {
      url: canvas.toDataURL('image/jpeg', JPEG_QUALITY),
      width,
      height,
    }
    canvas.width = 0
    canvas.height = 0
    return image
  }

  async function imageToDataUrl(src: string) {
    if(!src) return ''

    return new Promise<string>((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth || img.width
        canvas.height = img.naturalHeight || img.height
        const context = canvas.getContext('2d')
        if(!context) {
          reject(new Error('Canvas context is unavailable'))
          return
        }
        context.drawImage(img, 0, 0)
        const dataUrl = canvas.toDataURL('image/png')
        canvas.width = 0
        canvas.height = 0
        resolve(dataUrl)
      }
      img.onerror = () => reject(new Error(`Image load failed: ${src}`))
      img.src = src
    })
  }

  function textToDataUrl(text: string, backgroundColor = '#1890ff') {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if(!ctx) return ''

    const fontSize = 18
    const paddingX = 8
    const paddingY = 6
    ctx.font = `500 ${fontSize}px Arial, "Microsoft YaHei", sans-serif`
    const metrics = ctx.measureText(text)
    const width = Math.ceil(metrics.width + paddingX * 2)
    const height = Math.ceil(fontSize + paddingY * 2)
    canvas.width = width
    canvas.height = height

    const radius = 6
    ctx.clearRect(0, 0, width, height)
    ctx.font = `500 ${fontSize}px Arial, "Microsoft YaHei", sans-serif`
    ctx.fillStyle = backgroundColor
    ctx.beginPath()
    ctx.moveTo(radius, 0)
    ctx.lineTo(width - radius, 0)
    ctx.quadraticCurveTo(width, 0, width, radius)
    ctx.lineTo(width, height - radius)
    ctx.quadraticCurveTo(width, height, width - radius, height)
    ctx.lineTo(radius, height)
    ctx.quadraticCurveTo(0, height, 0, height - radius)
    ctx.lineTo(0, radius)
    ctx.quadraticCurveTo(0, 0, radius, 0)
    ctx.closePath()
    ctx.fill()
    ctx.fillStyle = '#ffffff'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, paddingX, height / 2 + 1)
    return canvas.toDataURL('image/png')
  }

  async function generateCardImagePDF(
    children: HTMLElement[],
    cols: number,
    titleBackgroundColor = '#1890ff',
    onProgress: (completed: number, total: number) => void = (completed, total) => {
      updateExportProgress(Math.round((completed / total) * 85), completed)
    },
  ) {
    if(!jsPDF) throw new Error('jsPDF is unavailable')

    const pdf = new jsPDF('p', 'mm', 'a4')
    const pxToMm = CONTENT_WIDTH / EXPORT_WIDTH
    const cardGap = CARD_GAP_PX * pxToMm
    const startY = PAGE_MARGIN + HEADER_HEIGHT_PX * pxToMm
    const pageBottom = A4_HEIGHT - PAGE_MARGIN
    let logoDataUrl = ''
    const titleDataUrl = textToDataUrl(titleText, titleBackgroundColor)
    let headerAdded = false

    try {
      logoDataUrl = await imageToDataUrl(logo)
      ensureExportActive()
    }catch(error) {
      if((error as Error).message === 'PDF_EXPORT_CANCELLED') throw error
      console.warn('PDF logo could not be loaded', error)
      logoDataUrl = ''
    }

    const addHeader = () => {
      if(headerAdded) return

      if(logoDataUrl) {
        const logoProps = pdf.getImageProperties(logoDataUrl)
        const logoW = 100 * pxToMm
        const logoH = logoProps.height ? logoW * (logoProps.height / logoProps.width) : logoW
        pdf.addImage(logoDataUrl, 'PNG', PAGE_MARGIN, PAGE_MARGIN + 40 * pxToMm, logoW, logoH)
      }
      if(titleDataUrl) {
        const titleProps = pdf.getImageProperties(titleDataUrl)
        const titleW = Math.min(titleProps.width * pxToMm, CONTENT_WIDTH)
        const titleH = titleProps.width ? titleW * (titleProps.height / titleProps.width) : 0
        pdf.addImage(titleDataUrl, 'PNG', PAGE_MARGIN, PAGE_MARGIN + 108 * pxToMm, titleW, titleH)
      }

      headerAdded = true
    }

    addHeader()
    let currentY = startY

    for(let i = 0; i < children.length; i += cols) {
      const rowElements = children.slice(i, i + cols)
      const rowItems: CardImage[] = []
      for(const element of rowElements) {
        rowItems.push(await elementToImage(element))
      }
      ensureExportActive()
      const rowHeights = rowItems.map((item) => item.height * pxToMm)
      const rowHeight = Math.max(...rowHeights, 0)
      const availablePageHeight = pageBottom - startY
      const rowScale = rowHeight > availablePageHeight ? availablePageHeight / rowHeight : 1
      const scaledRowHeight = rowHeight * rowScale

      if(currentY !== startY && currentY + scaledRowHeight > pageBottom) {
        pdf.addPage()
        headerAdded = false
        addHeader()
        currentY = startY
      }

      for(let col = 0; col < rowItems.length; col++) {
        const item = rowItems[col]
        const itemWidth = item.width * pxToMm * rowScale
        const itemHeight = item.height * pxToMm * rowScale
        const x = PAGE_MARGIN + col * ((item.width * pxToMm) + cardGap)
        pdf.addImage(item.url, 'JPEG', x, currentY, itemWidth, itemHeight)
      }
      currentY += scaledRowHeight + cardGap
      onProgress(Math.min(i + cols, children.length), children.length)
      await yieldToBrowser()
    }

    return pdf.output('arraybuffer') as ArrayBuffer
  }

  async function generatePDF(id: string) {
    const container = document.getElementById(id)
    if(!container || !container.children.length) {
      message.warning(intl.formatMessage({ id: '请先搜索导出数据' }))
      return
    }

    setLoading(true)
    cancelledRef.current = false
    const exportTotal = cardItems.length
    totalCountRef.current = exportTotal
    setTotalCount(exportTotal)
    setProcessedCount(0)
    setProgress(1)
    let objectUrl = ''
    let tempContainer: HTMLDivElement | null = null
    let cleanupTempContainer: (() => void) | null = null

    try {
      const children = Array.from(container.children) as HTMLElement[]

      if(useIndependentCardExport && !usePaginatedPreview) {
        const pdfData = await generateCardImagePDF(
          children,
          4,
          '#1890ff',
        )
        objectUrl = URL.createObjectURL(new Blob([pdfData], { type: 'application/pdf' }))
        objectUrlsRef.current.add(objectUrl)
        updateExportProgress(100, exportTotal)
        downloadFile(objectUrl)
        return
      }

      const mergedPdf = await PDFDocument.create()

      if(useIndependentCardExport) {
        await appendIndependentCardPDFs(mergedPdf, cardItems)
      }else if(isCardLayout) {
        await appendCardPagePDFs(mergedPdf, cardItems)
      }else if(shownum === 1 && usePaginatedPreview) {
        await appendStyleOnePDFs(mergedPdf, list)
      }else {
        const chunkSize = 40
        const batchCount = Math.ceil(children.length / chunkSize)

        for(let i = 0; i < children.length; i += chunkSize) {
          tempContainer = document.createElement('div')
          tempContainer.className = styles['products']
          tempContainer.style.position = 'fixed'
          tempContainer.style.left = '-10000px'
          tempContainer.style.top = '0'
          tempContainer.style.backgroundColor = '#FFFFFF'

          if(i === 0) {
            const logoElement = document.createElement('img')
            logoElement.src = logo
            logoElement.style.height = '60px'
            logoElement.className = styles['page-logo']
            tempContainer.appendChild(logoElement)
            tempContainer.style.paddingTop = '170px'

            const titleElement = document.createElement('div')
            titleElement.innerText = titleText
            titleElement.className = styles['sp']
            tempContainer.appendChild(titleElement)
          }

          for(const child of children.slice(i, i + chunkSize)) {
            tempContainer.appendChild(child.cloneNode(true))
          }
          document.body.appendChild(tempContainer)
          const currentContainer = tempContainer
          cleanupTempContainer = registerCleanup(() => currentContainer.remove())
          await waitForContainerAssets(currentContainer)
          const pdfData = await containerToPdfData(currentContainer)
          await appendPdfData(mergedPdf, pdfData)
          const batchIndex = Math.floor(i / chunkSize) + 1
          const processed = Math.min(batchIndex * chunkSize, exportTotal)
          updateExportProgress(Math.round((batchIndex / batchCount) * 90), processed)
          cleanupTempContainer()
          cleanupTempContainer = null
          tempContainer = null
          await yieldToBrowser()
          ensureExportActive()
        }
      }

      updateExportProgress(95, exportTotal)
      const mergedPdfBytes = await mergedPdf.save({ useObjectStreams: true })
      ensureExportActive()
      objectUrl = URL.createObjectURL(new Blob([mergedPdfBytes], { type: 'application/pdf' }))
      objectUrlsRef.current.add(objectUrl)
      updateExportProgress(100, exportTotal)
      downloadFile(objectUrl)
    }catch(error) {
      if((error as Error).message !== 'PDF_EXPORT_CANCELLED') {
        console.error('PDF export failed', error)
        message.error(intl.formatMessage({ id: '导出失败' }))
      }
    }finally {
      cleanupTempContainer?.()
      tempContainer?.remove()
      if(!cancelledRef.current) {
        setLoading(false)
        setProgress(0)
        setProcessedCount(0)
        setTotalCount(0)
      }
      if(objectUrl) {
        window.setTimeout(() => {
          URL.revokeObjectURL(objectUrl)
          objectUrlsRef.current.delete(objectUrl)
        }, PDF_URL_REVOKE_DELAY)
      }
    }
  }

  return (
    <Modal
      title={
        <div className="flex-b-c">
          <div className="flex-s-c">
            <label style={{ fontSize: 16 }}>{intl.formatMessage({ id: '预览' })}</label>
            <span>({intl.formatMessage({ id: '导出时保持处于当前页面' })})</span>
          </div>
          <div className="flex-s-c">
            {loading ? (
              <>
                <span className={styles['export-count']}>{processedCount}/{totalCount}</span>
                <Progress type="circle" percent={progress} size={36} />
              </>
            ) : null}
            <Button type="primary" className="mr30 ml10" loading={loading} onClick={() => generatePDF('pdfContent')}>{intl.formatMessage({ id: '导出' })}</Button>
          </div>
        </div>
      }
      open={true}
      onCancel={() => setOpen(false)}
      destroyOnClose
      footer={null}
      width={1100}
      maskClosable={false}
      
    >
      {usePaginatedPreview && !(ishm && shownum === 2) ? (
        <Alert
          className="mb15"
          type="info"
          showIcon
          message={intl.formatMessage(
            { id: '共{n}条数据，预览已启用分页；导出时将按整页图片批量生成，以提升速度。' },
            { n: list.length },
          )}
        />
      ) : null}
      <div
        className={`${styles['pdfcon']} ${usePaginatedPreview ? styles['preview-scroll'] : ''}`}
        id="pdfcon"
      >
        {
          shownum === 3 ? (
            <>
              <div className={styles['page-logo-1']} style={{ left: 20 }}>
                <img alt="" style={{ height: 60 }} src={logo} />
              </div>
              <div  style={{ marginTop: 100 }}>
                <div className="flex-s-c">
                  <div className="plr15 ptb5" style={{ background: 'black', color: '#fff', borderRadius: 8, fontSize: 18, fontWeight: 500 }}>
                    {titleText}
                  </div>
                </div>
                <div id="pdfContent" className={styles['products']} style={{ marginTop: 20, width: 1000 }}>
                  {previewItems.map(renderStyleThreeCard)}
                </div>
              </div>
            </>
          ) : shownum === 2 ? (
            <>
              <div className={styles['page-logo-1']} style={{ left: 20 }}>
                <img alt="" style={{ height: 60 }} src={logo} />
              </div>
              <div  style={{ marginTop: 100 }}>
                <div className="flex-s-c">
                  <div className="plr15 ptb5" style={{ background: '#1890ff', color: '#fff', borderRadius: 8, fontSize: 18, fontWeight: 500 }}>
                    {titleText}
                  </div>
                </div>
                <div id="pdfContent" className={styles['products']} style={{ marginTop: 20, width: 1000 }}>
                  {previewItems.map(renderStyleTwoCard)}
                </div>
              </div>
              
            </>
          ): (
            <>
              <div className={styles['page-logo']}>
                <img alt="" style={{ height: 60 }} src={logo} />
              </div>
              <div className="flex-s-c" style={{ paddingTop: 50 }}>
                <div className="plr15 ptb5" style={{ background: '#1890ff', color: '#fff', borderRadius: 8, fontSize: 18, fontWeight: 500 }}>
                  {titleText}
                </div>
              </div>
              <div className={styles['products']} id="pdfContent">
                {previewItems.map(renderStyleOneCard)}
              </div>
            </>
          )
        }

        
      </div>
      {usePaginatedPreview ? (
        <div className={styles['preview-pagination']}>
          <Pagination
            current={previewPage}
            pageSize={PREVIEW_PAGE_SIZE}
            total={cardItems.length}
            showSizeChanger={false}
            showTotal={(total: number) => intl.formatMessage({id: '共{n}条'}, {n: total})}
            onChange={(page) => {
              setPreviewPage(page)
              document.getElementById('pdfcon')?.scrollTo({ top: 0 })
            }}
          />
        </div>
      ) : null}
    </Modal>
    
  )
}

export default Pdfcon
